import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogContext";
import { Plus, Megaphone } from "@phosphor-icons/react";
import RichTextEditor from "@/components/RichTextEditor";
import SafeHtml from "@/components/SafeHtml";

const EMPTY_FORM = { title: "", body: "", target_role: "all", expires_at: "" };

export default function Announcements() {
  const { user } = useAuth();
  const { confirm } = useDialog();
  const canPost = user.role !== "employee";
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => { const r = await api.get("/announcements"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const closeCreate = () => {
    setShowCreate(false);
    setForm(EMPTY_FORM);
    setError("");
  };

  const bodyIsEmpty = () => {
    const stripped = (form.body || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    return stripped.length === 0;
  };

  const create = async (e) => {
    e.preventDefault(); setError("");
    if (!form.title.trim()) { setError("Title is required."); return; }
    if (bodyIsEmpty()) { setError("Message body cannot be empty."); return; }
    setSubmitting(true);
    try {
      await api.post("/announcements", {
        ...form,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      });
      closeCreate();
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (a) => {
    const ok = await confirm({
      title: "Delete announcement?",
      description: `“${a.title}” will be removed for everyone. This cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Keep",
      variant: "destructive",
    });
    if (!ok) return;
    await api.delete(`/announcements/${a.id}`);
    load();
  };

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Company / Announcements</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Announcements</h1>
          <p className="text-sm text-muted-foreground mt-2">Company-wide and role-targeted updates.</p>
        </div>
        {canPost && (
          <button className="btn-primary flex items-center gap-2" onClick={() => setShowCreate(true)} data-testid="create-announcement-button">
            <Plus size={16} weight="bold" /> New post
          </button>
        )}
      </div>
      <div className="p-8 lg:p-12 space-y-4">
        {items.map(a => (
          <div key={a.id} className="border border-border p-6" data-testid={`announcement-${a.id}`}>
            <div className="flex items-start justify-between mb-3 gap-4">
              <div className="flex items-center gap-3">
                <Megaphone size={20} weight="bold" className="text-[#002FA7]" />
                <div>
                  <div className="font-display text-xl font-bold">{a.title}</div>
                  <div className="text-xs text-muted-foreground font-mono-mini">
                    {a.author_name} · {a.author_role} · {new Date(a.created_at).toLocaleDateString()} · target: {a.target_role}
                  </div>
                </div>
              </div>
              {canPost && (
                <button
                  onClick={() => remove(a)}
                  className="text-xs text-muted-foreground hover:text-[#FF3B30]"
                  data-testid={`delete-announcement-${a.id}`}
                >
                  Delete
                </button>
              )}
            </div>
            <SafeHtml
              html={a.body}
              className="text-sm leading-relaxed"
              data-testid={`announcement-body-${a.id}`}
            />
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-sm text-muted-foreground py-12">No announcements yet.</div>}
      </div>

      {showCreate && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={closeCreate}
        >
          <div
            className="bg-white border border-border w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="create-announcement-dialog"
          >
            <div className="eyebrow mb-2">Post</div>
            <h2 className="font-display text-2xl font-bold mb-6">New announcement</h2>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="eyebrow block mb-2">Title</label>
                <input
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full border border-border px-3 py-2 text-sm"
                  data-testid="announcement-title-input"
                />
              </div>
              <div>
                <label className="eyebrow block mb-2">Message</label>
                <RichTextEditor
                  value={form.body}
                  onChange={(html) => setForm((f) => ({ ...f, body: html }))}
                  placeholder="Share the update with your team…"
                  minHeight={200}
                  testid="announcement-body-input"
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Use the toolbar for headings, lists, quotes, links, and inline formatting.
                </p>
              </div>
              <div>
                <label className="eyebrow block mb-2">Target</label>
                <select
                  value={form.target_role}
                  onChange={(e) => setForm({ ...form, target_role: e.target.value })}
                  className="w-full border border-border px-3 py-2 text-sm bg-white"
                  data-testid="announcement-target-select"
                >
                  <option value="all">Everyone</option>
                  <option value="manager">Managers</option>
                  <option value="hr">HR</option>
                  <option value="employee">Employees</option>
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-2">Expires at (optional)</label>
                <input
                  type="datetime-local"
                  value={form.expires_at || ""}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="w-full border border-border px-3 py-2 text-sm"
                  data-testid="announcement-expires-input"
                />
              </div>
              {error && <div className="text-sm text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeCreate}
                  className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 btn-primary disabled:opacity-60"
                  data-testid="submit-announcement"
                >
                  {submitting ? "Posting…" : "Post"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
